// The console's frame: the menu on the left (the System pages for QOR's own staff), the top bar with the processor
// in view and who is signed in, then the page.
import {
  Icon,
  initials,
  useBodyClass,
  useBrand,
  useSession,
  useSetSession,
  useTitle,
  words,
  type IconName,
  type SessionView,
} from '@qor/web-shared';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { adminApi } from '../api';
import { PROVIDER_OPTIONS, scopeOf } from '../scope';

/** Active on its page and the pages under it (/cardholders/123), except the ones another entry stands for. */
function NavLink({
  to,
  except = [],
  children,
  icon,
}: {
  to: string;
  except?: string[];
  children: ReactNode;
  icon: IconName;
}) {
  const current = useLocation().pathname.toLowerCase();
  const under = (p: string) => current === p || current.startsWith(`${p}/`);
  const active = under(to) && !except.some(under);
  return (
    <Link
      className={`qc-nav-link ${active ? 'qc-nav-link-active' : ''}`}
      to={to}
      aria-current={active ? 'page' : undefined}
    >
      <Icon name={icon} />
      <span>{children}</span>
    </Link>
  );
}

function ProviderSwitcher({ view }: { view: SessionView }) {
  const setSession = useSetSession();
  const client = useQueryClient();
  const [busy, setBusy] = useState(false);
  async function choose(provider: string) {
    if (!(PROVIDER_OPTIONS as readonly string[]).includes(provider)) return;
    setBusy(true);
    try {
      if (view.user) await adminApi.setActiveProvider(view.user.email, provider);
      setSession({ ...view, activeProvider: provider });
      // every page reads again, scoped to the new processor
      await client.invalidateQueries({ predicate: (q) => q.queryKey[0] !== 'bff' });
    } finally {
      setBusy(false);
    }
  }
  return (
    <form className="qc-provider-switcher" onSubmit={(e) => e.preventDefault()}>
      <label htmlFor="qc-provider-select">Processor</label>
      <select
        name="provider"
        id="qc-provider-select"
        value={view.activeProvider ?? ''}
        disabled={busy}
        onChange={(e) => void choose(e.target.value)}
      >
        <option value="">Choose a processor...</option>
        {PROVIDER_OPTIONS.map((p) => (
          <option key={p} value={p}>
            {p}
          </option>
        ))}
      </select>
    </form>
  );
}

export function ConsoleLayout() {
  const session = useSession();
  const view = session.data;
  const user = view?.user ?? null;
  const scope = scopeOf(view);
  const brand = useBrand(user?.email);
  const location = useLocation();
  // open on the page it was opened on: following a link closes it
  const [openOn, setOpenOn] = useState<string | null>(null);
  const menuOpen = openOn === location.pathname;
  useBodyClass('qc-body');
  useEffect(() => {
    if (!menuOpen) return;
    const close = (e: KeyboardEvent) => e.key === 'Escape' && setOpenOn(null);
    document.addEventListener('keydown', close);
    return () => document.removeEventListener('keydown', close);
  }, [menuOpen]);
  // the menu depends on who is signed in: shown once that is known (no flash of the signed-out menu)
  if (session.isPending) return null;

  return (
    <>
      <a className="qc-skip-link" href="#qc-main">
        Skip to content
      </a>

      <aside className={`qc-sidebar ${menuOpen ? 'qc-sidebar-open' : ''}`} id="qc-sidebar">
        <Link className="qc-sidebar-brand" to="/dashboard" aria-label={`${brand.displayName} Console dashboard`}>
          <img src="/images/qor-logo-mark.png" alt={brand.displayName} />
        </Link>

        <nav className="qc-nav qc-nav-main" aria-label="Console navigation">
          <p className="qc-nav-label">Program</p>
          <NavLink to="/dashboard" icon="dashboard">
            Dashboard
          </NavLink>
          <NavLink to="/cardholders" icon="people">
            Cardholders
          </NavLink>
          <NavLink to="/transactions" icon="transfer">
            Transactions
          </NavLink>
          <NavLink to="/loads" icon="card">
            Loads
          </NavLink>
          <NavLink to="/points" except={['/points/settings']} icon="coins">
            Points to Cash
          </NavLink>
          <NavLink to="/points/settings" icon="sliders">
            Points Configuration
          </NavLink>

          {scope.internal && (
            <>
              <p className="qc-nav-label">System</p>
              <NavLink to="/funding" icon="coins">
                Funding
              </NavLink>
              <NavLink to="/partners" icon="briefcase">
                Partners
              </NavLink>
              <NavLink to="/users" icon="person">
                Users
              </NavLink>
              <NavLink to="/audit-log" icon="document">
                Audit Log
              </NavLink>
              <NavLink to="/processor-log" icon="server">
                Processor Log
              </NavLink>
              {/* QOR's account at InterCash: only while InterCash is the processor in view */}
              {scope.activeProvider === 'InterCash' && (
                <NavLink to="/intercash-account" icon="bank">
                  InterCash Account
                </NavLink>
              )}
              <NavLink to="/kiosk-simulator" icon="monitor">
                GSC Kiosk Simulator
              </NavLink>
            </>
          )}
        </nav>

        <nav className="qc-sidebar-footer" aria-label="Help and account">
          <NavLink to="/support" icon="support">
            Support
          </NavLink>
          <NavLink to="/security" icon="shield">
            Security
          </NavLink>
          <NavLink to="/logout" icon="signOut">
            Sign Out
          </NavLink>
          <div className="qc-legal-links">
            <Link to="/privacy">Privacy Policy</Link>
            <span aria-hidden="true">&middot;</span>
            <Link to="/terms">Terms of Service</Link>
          </div>
        </nav>
      </aside>

      {menuOpen && (
        <button
          type="button"
          className="qc-sidebar-scrim"
          aria-label="Close navigation"
          onClick={() => setOpenOn(null)}
        ></button>
      )}

      <div className="qc-shell">
        <header className="qc-topbar">
          <button
            type="button"
            className="qc-topbar-toggle"
            aria-label="Toggle navigation"
            aria-expanded={menuOpen}
            aria-controls="qc-sidebar"
            onClick={() => setOpenOn(menuOpen ? null : location.pathname)}
          >
            <Icon name={menuOpen ? 'close' : 'menu'} />
          </button>
          {scope.internal && view && <ProviderSwitcher view={view} />}
          <div className="qc-topbar-spacer"></div>
          <div className="qc-topbar-actions">
            {user ? (
              <div className="qc-user">
                <span className="qc-user-avatar" aria-hidden="true">
                  {initials(user.displayName)}
                </span>
                <span className="qc-user-text">
                  <span className="qc-user-name">{user.displayName}</span>
                  <span className="qc-user-role">{words(user.role)}</span>
                </span>
              </div>
            ) : (
              <Link className="qc-topbar-link" to="/login">
                Sign in
              </Link>
            )}
          </div>
        </header>

        <main className="qc-content" id="qc-main" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
    </>
  );
}

/** "<page> - <brand> Console" */
export function usePageTitle(title: string) {
  const session = useSession();
  const brand = useBrand(session.data?.user?.email);
  useTitle(title, { displayName: `${brand.displayName} Console` });
}
